import { relicsPool } from '../data/relics';
import {
  hydrateSpiderBossState,
  serializeSpiderBossState,
} from '../boss/spiderBossSystem';
import {
  getSpecialBossFloorEncounter,
  isSpecialBossFloor,
} from '../boss/specialBossFloorConfig';

// Import deferred storage helpers. These functions allow writes to be
// coalesced and flushed during idle time, avoiding synchronous
// serialization inside render/layout effects. Immediate writes are still
// available for critical flushes such as before navigation.
import {
  flushPendingStorageWrites,
  queueJsonStorageWrite,
  queueRawStorageWrite,
  removeStorageItemNow,
  writeJsonStorageNow,
  writeRawStorageNow,
} from './storageQueue';

export const CHARACTER_KEY = 'dd_selected_character_v1';
export const MAIN_MENU_KEY = 'dd_main_menu_seen_v1';
export const FLOOR_LEVEL_KEY = 'dd_floor_level_v1';
export const RUN_SEED_KEY = 'dd_run_seed_v1';
export const RANSACKED_CHEST_SPAWNED_KEY = 'dd_ransacked_chest_spawned_v1';
export const FLOOR_SNAPSHOT_KEY = 'dd_floor_snapshot_v1';
export const TEST_RUN_CONFIG_KEY = 'dd_test_run_config_v1';

const asCoord = (value) => {
  if (!value || typeof value !== 'object') return null;
  const x = Math.floor(Number(value.x));
  const y = Math.floor(Number(value.y));
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
};

const asStringArray = (value) =>
  Array.isArray(value) ? value.filter((v) => typeof v === 'string') : [];

const hydrateYahakaState = (value) => {
  if (!value || typeof value !== 'object') return null;

  const coord = asCoord(value);
  return {
    active: !!value.active && !!coord,
    x: coord?.x ?? null,
    y: coord?.y ?? null,
  };
};

const hydrateRelicOffers = (ids) =>
  asStringArray(ids)
    .map((id) => relicsPool.find((r) => r.id === id))
    .filter(Boolean);

const hydrateSpecialBossFloorSelection = (value, floorLevel) => {
  if (!value || typeof value !== 'object') return null;

  const normalizedFloor = Math.max(1, Math.floor(Number(floorLevel) || 1));
  if (!isSpecialBossFloor(normalizedFloor)) return null;

  const selectionFloor = Math.floor(Number(value.floor));
  if (Number.isFinite(selectionFloor) && selectionFloor !== normalizedFloor) {
    return null;
  }

  return getSpecialBossFloorEncounter(
    {
      ...value,
      floor: normalizedFloor,
    },
    { floor: normalizedFloor }
  );
};

const serializeFloorCorruption = (value) => {
  if (!value || typeof value !== 'object') return null;

  return {
    ...value,
    corruptedKeys:
      value.corruptedKeys instanceof Set ? [...value.corruptedKeys] : [],
    specialEnemyKeys:
      value.specialEnemyKeys instanceof Set ? [...value.specialEnemyKeys] : [],
    exposureByKey:
      value.exposureByKey && typeof value.exposureByKey === 'object'
        ? value.exposureByKey
        : {},
  };
};

const hydrateFloorCorruption = (value) => {
  if (!value || typeof value !== 'object') return null;

  return {
    ...value,
    corruptedKeys: new Set(asStringArray(value.corruptedKeys)),
    specialEnemyKeys: new Set(asStringArray(value.specialEnemyKeys)),
    exposureByKey:
      value.exposureByKey && typeof value.exposureByKey === 'object'
        ? value.exposureByKey
        : {},
  };
};

const asSpawnTutorialStage = (value, fallback = 'lightTorch') => {
  return value === 'lightTorch' || value === 'moveInteract' || value === 'findExit' || value === 'done'
    ? value
    : fallback;
};



const clampWhole = (value, fallback, min = 0, max = Number.MAX_SAFE_INTEGER) => {
  const parsed = Math.floor(Number(value));
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, Math.min(max, parsed));
};

const sanitizeTestRunConfig = (value) => {
  const raw = value && typeof value === 'object' ? value : {};

  const deckIds = Array.isArray(raw.deckIds)
    ? raw.deckIds.map((v) => String(v ?? '').trim()).filter(Boolean)
    : [];

  const relicIds = Array.isArray(raw.relicIds)
    ? Array.from(new Set(raw.relicIds.map((v) => String(v ?? '').trim()).filter(Boolean)))
    : [];

  return {
    floorLevel: clampWhole(raw.floorLevel, 1, 1, 999),
    hp: clampWhole(raw.hp, 100, 1, 999),
    sp: clampWhole(raw.sp, 100, 1, 999),
    deckIds,
    relicIds,
  };
};

export function readTestRunConfig() {
  try {
    const raw = localStorage.getItem(TEST_RUN_CONFIG_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return sanitizeTestRunConfig(parsed);
  } catch {
    return sanitizeTestRunConfig(null);
  }
}

export function writeTestRunConfig(config) {
  try {
    localStorage.setItem(TEST_RUN_CONFIG_KEY, JSON.stringify(sanitizeTestRunConfig(config)));
  } catch {
    // ignore
  }
}

export function clearTestRunConfig() {
  try {
    localStorage.removeItem(TEST_RUN_CONFIG_KEY);
  } catch {
    // ignore
  }
}

export function readSavedFloorLevel() {
  try {
    const raw = localStorage.getItem(FLOOR_LEVEL_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const n = Math.max(1, Math.floor(Number(parsed) || 1));
    return Number.isFinite(n) ? n : 1;
  } catch {
    return 1;
  }
}

export function makeFreshRunSeed() {
  return (Math.floor(Math.random() * 1_000_000_000)) >>> 0;
}

export function readSavedRunSeed() {
  try {
    const raw = localStorage.getItem(RUN_SEED_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const n = Math.floor(Number(parsed));
    if (Number.isFinite(n) && n >= 0) return n >>> 0;
  } catch {}
  return makeFreshRunSeed();
}

export function readRansackedChestSpawned() {
  try {
    return localStorage.getItem(RANSACKED_CHEST_SPAWNED_KEY) === '1';
  } catch {
    return false;
  }
}

export function readSavedFloorSnapshot() {
  try {
    const raw = localStorage.getItem(FLOOR_SNAPSHOT_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return null;

    return {
      runSeed: Math.floor(Number(parsed.runSeed) || 0) >>> 0,
      floorLevel: Math.max(1, Math.floor(Number(parsed.floorLevel) || 1)),
      currentFloorMood: parsed.currentFloorMood ?? null,
      currentFloorTorchDuration: Math.max(
        1,
        Math.floor(Number(parsed.currentFloorTorchDuration) || 0)
      ),
      turns: Math.max(0, Math.floor(Number(parsed.turns) || 0)),
      playerPos: asCoord(parsed.playerPos),
      map: Array.isArray(parsed.map) ? parsed.map : null,
      barredDoorKeys: new Set(asStringArray(parsed.barredDoorKeys)),
      exploredTiles: new Set(asStringArray(parsed.exploredTiles)),
      litBraziers: new Set(asStringArray(parsed.litBraziers)),
      floorActionCount: Math.max(0, Math.floor(Number(parsed.floorActionCount) || 0)),
      floorCorruption: hydrateFloorCorruption(parsed.floorCorruption),
      specialBossFloorSelection: hydrateSpecialBossFloorSelection(
        parsed.specialBossFloorSelection,
        parsed.floorLevel
      ),
      spiderBossState: hydrateSpiderBossState(parsed.spiderBossState),
      torches: Math.max(0, Math.floor(Number(parsed.torches) || 0)),
      keys: Math.max(0, Math.floor(Number(parsed.keys) || 0)),
      teeth: Math.max(0, Math.floor(Number(parsed.teeth) || 0)),
      potions: Math.max(0, Math.floor(Number(parsed.potions) || 0)),
      slainEnemiesThisFloor: Math.max(
        0,
        Math.floor(Number(parsed.slainEnemiesThisFloor) || 0)
      ),
      brazierQuestCompletionTriggered: !!parsed.brazierQuestCompletionTriggered,
      brazierCompletionBannerText:
        typeof parsed.brazierCompletionBannerText === 'string'
          ? parsed.brazierCompletionBannerText
          : '',
      cleansingQuestCompletionTriggered: !!parsed.cleansingQuestCompletionTriggered,
      cleansingCompletionBannerText:
        typeof parsed.cleansingCompletionBannerText === 'string'
          ? parsed.cleansingCompletionBannerText
          : '',
      pendingDeckCullReward: !!parsed.pendingDeckCullReward,
      pendingCorruptionBonusReward: !!parsed.pendingCorruptionBonusReward,
      showSpawnTorchHint: parsed.showSpawnTorchHint !== false,
      spawnTutorialStage: asSpawnTutorialStage(
        parsed.spawnTutorialStage,
        parsed.showSpawnTorchHint !== false ? 'lightTorch' : 'done'
      ),
      spawnTutorialMoveTriggered: !!parsed.spawnTutorialMoveTriggered,
      nextCombatCost0: Math.max(0, Math.floor(Number(parsed.nextCombatCost0) || 0)),
nextCombatNegateNextDamage: Math.max(
  0,
  Math.floor(Number(parsed.nextCombatNegateNextDamage) || 0)
),
nextCombatStartArmor: Math.max(
  0,
  Math.floor(Number(parsed.nextCombatStartArmor) || 0)
),
bypassEnemiesRemaining: Math.max(
  0,
  Math.floor(Number(parsed.bypassEnemiesRemaining) || 0)
),
freeChestsRemaining: Math.max(
  0,
  Math.floor(Number(parsed.freeChestsRemaining) || 0)
),
      pendingRansackedChestPos: asCoord(parsed.pendingRansackedChestPos),
pendingWellMythicPos: asCoord(parsed.pendingWellMythicPos),
merchantStock:
  parsed.merchantStock && typeof parsed.merchantStock === 'object'
    ? parsed.merchantStock
    : null,

relicOfferIds: hydrateRelicOffers(parsed.relicOfferIds),
isWellEchoOpen: !!parsed.isWellEchoOpen,
revealedSanctifiedSecretTargetKey:
  typeof parsed.revealedSanctifiedSecretTargetKey === 'string'
    ? parsed.revealedSanctifiedSecretTargetKey
    : '',
claimedSanctifiedSecretTargetKey:
  typeof parsed.claimedSanctifiedSecretTargetKey === 'string'
    ? parsed.claimedSanctifiedSecretTargetKey
    : '',
tarot:
  parsed.tarot && typeof parsed.tarot === 'object'
    ? parsed.tarot
    : null,
      corruptionBannerText:
        typeof parsed.corruptionBannerText === 'string'
          ? parsed.corruptionBannerText
          : '',
      yahakaState: hydrateYahakaState(parsed.yahakaState),
      yahakaDefeatCount: Math.max(
        0,
        Math.min(7, Math.floor(Number(parsed.yahakaDefeatCount) || 0))
      ),
      yahakaSpawnArmed:
        typeof parsed.yahakaSpawnArmed === 'boolean'
          ? parsed.yahakaSpawnArmed
          : true,
      yahakaBannerText:
        typeof parsed.yahakaBannerText === 'string'
          ? parsed.yahakaBannerText
          : '',
    };
  } catch {
    return null;
  }
}

export function writeMainMenuSeen(seen) {
  try {
    localStorage.setItem(MAIN_MENU_KEY, seen ? '1' : '0');
  } catch {
    // ignore
  }
}

export function writeSelectedCharacter(id) {
  try {
    if (id === 'fate' || id === 'fame' || id === 'fortune') {
      localStorage.setItem(CHARACTER_KEY, id);
      return;
    }

    localStorage.removeItem(CHARACTER_KEY);
    localStorage.removeItem('deckdelve.selectedCharacter.v1'); // old compatibility key
  } catch {
    // ignore
  }
}

export function writeSavedFloorLevel(floorLevel, { immediate = false } = {}) {
  const value = Math.max(1, Math.floor(Number(floorLevel) || 1));

  if (immediate) {
    writeJsonStorageNow(FLOOR_LEVEL_KEY, value);
    return;
  }

  queueJsonStorageWrite(FLOOR_LEVEL_KEY, value);
}

export function writeSavedRunSeed(runSeed, { immediate = false } = {}) {
  const value = (Math.floor(Number(runSeed) || 0)) >>> 0;

  if (immediate) {
    writeJsonStorageNow(RUN_SEED_KEY, value);
    return;
  }

  queueJsonStorageWrite(RUN_SEED_KEY, value);
}

export function writeRansackedChestSpawned(spawned, { immediate = false } = {}) {
  const value = spawned ? '1' : '0';

  if (immediate) {
    writeRawStorageNow(RANSACKED_CHEST_SPAWNED_KEY, value);
    return;
  }

  queueRawStorageWrite(RANSACKED_CHEST_SPAWNED_KEY, value);
}

export function writeFloorSnapshot(snapshot, { immediate = false } = {}) {
  try {
    const safeSnapshot = {
      ...snapshot,
      floorCorruption: serializeFloorCorruption(snapshot?.floorCorruption),
      specialBossFloorSelection:
        snapshot?.specialBossFloorSelection && typeof snapshot.specialBossFloorSelection === 'object'
          ? snapshot.specialBossFloorSelection
          : null,
      spiderBossState: serializeSpiderBossState(snapshot?.spiderBossState),
    };

    if (immediate) {
      writeJsonStorageNow(FLOOR_SNAPSHOT_KEY, safeSnapshot);
      return;
    }

    queueJsonStorageWrite(FLOOR_SNAPSHOT_KEY, safeSnapshot);
  } catch {
    // ignore
  }
}

export function clearSavedRunState({
  clearCharacter = false,
  clearMenuSeen = false,
} = {}) {
  try {
    removeStorageItemNow('dd_owned_relics_v1');
    removeStorageItemNow('dd_player_deck_ids_v1');
    removeStorageItemNow('dd_player_hp_v1');
    removeStorageItemNow('dd_player_sp_v1');
    removeStorageItemNow('dd_player_level_v1');
    removeStorageItemNow('dd_player_xp_v1');
    removeStorageItemNow('dd_level_maxhp_bonus_v1');
    removeStorageItemNow('dd_level_maxsp_bonus_v1');
    removeStorageItemNow('dd_shrine_maxhp_penalty_v1');
    removeStorageItemNow('dd_shrine_maxsp_penalty_v1');
    removeStorageItemNow('dd_story_flags_v1');

    removeStorageItemNow(FLOOR_LEVEL_KEY);
    removeStorageItemNow(RUN_SEED_KEY);
    removeStorageItemNow(RANSACKED_CHEST_SPAWNED_KEY);
    removeStorageItemNow(FLOOR_SNAPSHOT_KEY);
    removeStorageItemNow(TEST_RUN_CONFIG_KEY);

    if (clearCharacter) {
      removeStorageItemNow(CHARACTER_KEY);
      removeStorageItemNow('deckdelve.selectedCharacter.v1'); // old compatibility key
    }

    if (clearMenuSeen) {
      removeStorageItemNow(MAIN_MENU_KEY);
    }
  } catch {
    // ignore
  }
}

export function hasSavedContinueRun() {
  const snapshot = readSavedFloorSnapshot();
  const character = readSelectedCharacter();

  try {
    return (
      !!character &&
      !!snapshot?.playerPos &&
      Array.isArray(snapshot?.map) &&
      snapshot.map.length > 0
    );
  } catch {
    return false;
  }
}

export function readMainMenuSeen() {
  try {
    return localStorage.getItem(MAIN_MENU_KEY) === '1';
  } catch {
    return false;
  }
}

export function readSelectedCharacter() {
  try {
    const raw = localStorage.getItem(CHARACTER_KEY);
    if (raw === 'fate' || raw === 'fame' || raw === 'fortune') return raw;
  } catch {}
  return null;
}